import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  Globe,
  Cpu,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Wind,
  Thermometer,
  Sun,
  Volume2,
  Gauge,
  Droplets,
  Database,
  LineChart,
  Brain,
  ShieldCheck,
  TrendingUp,
  Wrench,
  Leaf,
  Activity,
} from "lucide-react";

interface DataType {
  icon: React.ReactNode;
  name: string;
  desc: string;
}

interface WorkflowStep {
  icon: React.ReactNode;
  title: string;
  desc: string;
}

interface ValueItem {
  icon: React.ReactNode;
  title: string;
  desc: string;
}

interface ServiceDetail {
  slug: string;
  icon: React.ReactNode;
  title: string;
  badge: string;
  summary: string;
  highlights: string[];
  items: string[];
  scenarios: string[];
  gradient: string;
  dataTypes?: DataType[];
  workflow?: WorkflowStep[];
  values?: ValueItem[];
}

const services: ServiceDetail[] = [
  {
    slug: "building-environment",
    icon: <Building2 className="w-8 h-8 text-white" />,
    title: "建筑设施与环境数据服务",
    badge: "智慧建筑 · 绿色环境",
    summary:
      "面向智慧楼宇、产业园区与公共建筑场景，提供从数据治理、数字孪生到智能分析的全栈服务。基于客户现有的多维度环境与设备运行数据，帮助客户优化空间环境、提升运营效率与建筑能效。",
    highlights: [
      "环境数据处理",
      "异常预警预测",
      "设备预测维护",
      "能效优化分析",
    ],
    items: [
      "提供建筑设施数据治理与智能分析一体化服务",
      "依托客户提供的空气质量、温湿度、光照度、噪声、CO₂、PM2.5等环境指标数据，进行标准化治理与多维度分析",
      "基于分布式消息队列与时序数据库，实现外部数据的高吞吐接入、存储、压缩与高效检索",
      "提供数据清洗、启发式填补、异常点过滤与多源特征融合，保障分析输入数据的准确性与一致性",
      "基于深度学习时序分析技术（LSTM、Transformer等），实现环境异常自动预警与趋势预测",
      "构建设备健康度评估体系，对暖通、照明、给排水、电梯等设备进行预测性维护与剩余寿命评估",
      "辅助优化暖通空调系统运行策略，结合室外气象、室内人流与历史运行数据动态调节设定，提升建筑整体能效",
      "提供能耗分项计量与分析报表，识别高能耗区域与设备，为节能改造決策提供数据依据",
      "支持多组织、多项目级的集中化运营看板，实现实时状态、告警事件与关键指标的可视化呈现"
    ],
    scenarios: [
      "智慧楼宇",
      "产业园区",
      "医院与教育建筑",
      "商业综合体",
      "能效管理",
      "设备运维",
    ],
    gradient: "from-blue-500 to-cyan-500",
    workflow: [
      { icon: <Database className="w-6 h-6 text-white" />, title: "数据接入", desc: "多协议·多源数据接入，汇聚为统一的数据总线" },
      { icon: <LineChart className="w-6 h-6 text-white" />, title: "数据治理", desc: "清洗·填补·标准化，沉淀为可复用的数据资产" },
      { icon: <Brain className="w-6 h-6 text-white" />, title: "智能分析", desc: "深度学习·时序建模，实现异常识别与趋势预测" },
      { icon: <ShieldCheck className="w-6 h-6 text-white" />, title: "决策支撑", desc: "可视化报表·优化建议，赋能业务与运营管理" },
    ],
    values: [
      { icon: <TrendingUp className="w-6 h-6 text-blue-600" />, title: "能效提升 15%+", desc: "通过动态优化运行策略，显著降低建筑运营能耗" },
      { icon: <Wrench className="w-6 h-6 text-blue-600" />, title: "设备故障减少 30%", desc: "预测性维护取代事后维修，延长设备使用寿命" },
      { icon: <Activity className="w-6 h-6 text-blue-600" />, title: "室内环境达标率 99%", desc: "实现二氧化碳、PM2.5等指标的智能调节" },
      { icon: <Leaf className="w-6 h-6 text-blue-600" />, title: "可持续运营", desc: "为双碳目标与绿色运营提供可量化的数据支撑" },
    ],
  },
  {
    slug: "remote-sensing",
    icon: <Globe className="w-8 h-8 text-white" />,
    title: "遥感影像图像数据处理",
    badge: "遥感解译 · 地理信息",
    summary:
      "提供卫星、航空及无人机遥感影像的全链条处理解决方案，支持多源数据批量化预处理与智能解译。",
    highlights: ["多源数据处理", "辐射几何校正", "语义分割分类", "长时序变化检测"],
    items: [
      "提供卫星、航空及无人机遥感影像的全链条处理解决方案",
      "支持可见光、多光谱、雷达及激光点云等多源遥感数据的批量化预处理",
      "涵盖辐射校正、大气校正、几何校正与图像融合等关键环节",
      "部署深度学习语义分割模型，实现高精度的土地利用与覆盖分类",
      "结合植被指数、地表温度等参数，进行定量反演与长时间序列变化检测",
      "广泛应用于国土空间规划、自然资源调查、生态环境监测与智慧农业等领域",
    ],
    scenarios: ["国土空间规划", "自然资源调查", "生态环境监测", "智慧农业"],
    gradient: "from-indigo-500 to-blue-600",
  },
  {
    slug: "optical-simulation",
    icon: <Cpu className="w-8 h-8 text-white" />,
    title: "光学仿真数据建模与数字孪生",
    badge: "光学计算 · 数字孪生",
    summary:
      "将光学机理建模与大数据分析深度融合，基于自主光线追迹引擎构建光学系统性能数字孪生体。",
    highlights: ["光谱仿真建模", "数字孪生构建", "统计公差分析", "装配工艺优化"],
    items: [
      "将光学机理建模与大数据分析深度融合，为光学系统设计提供数据驱动的决策支撑",
      "基于自主光线追迹仿真引擎，对海量追迹数据进行统计建模与可视化分析，构建光学系统性能数字孪生体",
      "对反射率、透射率、吸收率等光谱参数进行高精度仿真建模，计算准确度达到行业领先水平",
      "通过统计公差分析，量化制造工艺误差对系统成像质量的统计影响",
      "为光学元件加工公差分配与装配工艺优化提供量化依据，服务于高端仪器装备制造与精密光学检测",
    ],
    scenarios: ["高端仪器制造", "精密光学检测", "光学系统设计", "工艺优化"],
    gradient: "from-cyan-500 to-blue-600",
  },
];

const DataServiceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  const service = useMemo(
    () => services.find((s) => s.slug === slug),
    [slug]
  );

  const others = useMemo(
    () => services.filter((s) => s.slug !== slug),
    [slug]
  );

  if (!service) {
    return (
      <Layout>
        <section className="py-32 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">未找到对应的服务模块</h2>
          <Button onClick={() => navigate("/")}>返回首页</Button>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section
        className={`relative overflow-hidden bg-gradient-to-br ${service.gradient} py-20`}
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-24 -left-10 w-72 h-72 bg-white/10 rounded-full"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/15 hover:text-white mb-6 -ml-3"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            返回
          </Button>
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
            <div className="p-5 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg flex-shrink-0">
              {service.icon}
            </div>
            <div className="flex-1">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                {service.badge}
              </Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-sm">
                {service.title}
              </h1>
              <p className="text-lg text-white/90 max-w-3xl leading-relaxed">
                {service.summary}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              核心能力亮点
            </h2>
            <p className="text-gray-500">聚焦关键技术与服务能力，为客户提供专业、可靠的解决方案</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {service.highlights.map((h, i) => (
              <Card
                key={i}
                className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-blue-50 to-white"
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="p-2.5 bg-blue-100 rounded-full mb-3">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-gray-800">
                    {h}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Types */}
      {service.dataTypes && service.dataTypes.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-blue-50/40 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                采集数据类型
              </h2>
              <p className="text-gray-500">多维度传感与计量数据，构建全面的建筑环境画像</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {service.dataTypes.map((d, i) => (
                <Card
                  key={i}
                  className="border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                >
                  <CardContent className="p-5 flex flex-col items-center text-center">
                    <div className="p-3 bg-blue-50 rounded-xl mb-3">{d.icon}</div>
                    <div className="text-base font-semibold text-gray-900 mb-1">{d.name}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{d.desc}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Workflow */}
      {service.workflow && service.workflow.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                服务流程
              </h2>
              <p className="text-gray-500">从数据到决策，构建完整闭环的智能分析服务体系</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
              {service.workflow.map((w, i) => (
                <div key={i} className="relative">
                  <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 h-full">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 shadow-md`}>
                        {w.icon}
                      </div>
                      <div className="text-xs text-blue-600 font-semibold mb-1">
                        STEP 0{i + 1}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{w.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                    </CardContent>
                  </Card>
                  {i < service.workflow!.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-5 h-5 text-blue-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Detail Items */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              服务详情
            </h2>
            <p className="text-gray-500">完整的服务体系与技术细节</p>
          </div>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6 md:p-10 space-y-5">
              {service.items.map((item, i) => (
                <div key={i} className="flex items-start">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-600 font-semibold text-sm flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="ml-4 text-gray-700 leading-relaxed text-base">
                    {item}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Scenarios */}
          <div className="mt-10">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
              典型应用场景
            </h3>
            <div className="flex flex-wrap gap-3">
              {service.scenarios.map((s, i) => (
                <Badge
                  key={i}
                  variant="secondary"
                  className="px-4 py-2 text-sm bg-white text-blue-700 hover:bg-blue-50 border border-blue-200 shadow-sm"
                >
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      {service.values && service.values.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                价值与收益
              </h2>
              <p className="text-gray-500">以数据驱动赋能建筑运营，创造可量化的业务价值</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.values.map((v, i) => (
                <Card
                  key={i}
                  className="border border-blue-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <div className="p-3 bg-blue-50 rounded-xl w-fit mb-4">{v.icon}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Services */}
      {others.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  探索更多服务
                </h2>
                <p className="text-gray-500">了解我们其他领域的数据服务能力</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {others.map((s) => (
                <Link
                  key={s.slug}
                  to={`/data-service/${s.slug}`}
                  className="group"
                >
                  <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                    <div
                      className={`relative h-24 bg-gradient-to-br ${s.gradient} flex items-center px-6 overflow-hidden`}
                    >
                      <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full"></div>
                      <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                        {s.icon}
                      </div>
                      <h3 className="ml-4 text-lg font-bold text-white drop-shadow-sm">
                        {s.title}
                      </h3>
                    </div>
                    <CardContent className="p-5 flex items-center justify-between">
                      <span className="text-sm text-gray-600 line-clamp-1">
                        {s.badge}
                      </span>
                      <span className="text-blue-600 font-medium flex items-center text-sm group-hover:translate-x-1 transition-transform">
                        查看详情 <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            想了解更多详情或定制方案？
          </h2>
          <p className="text-blue-100 mb-8">
            我们的专家团队将为您提供专业的咨询与一对一服务支持
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">
                联系我们 <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="bg-transparent text-white border-white hover:bg-white hover:text-blue-700"
            >
              <Link to="/">返回首页</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DataServiceDetail;
